import React, { useContext, useReducer, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import Form from 'react-bootstrap/Form';
import { MdArrowBack, MdSave } from 'react-icons/md';
import { Helmet } from 'react-helmet-async';
import { toast } from '../toasts';
import { Store } from '../Store';
import { EntityList, PageHead, Button, FormField, Section, controlClass } from '../components/EntityList';
import { ROLE_OPTIONS } from '../constants/roles';
import { getError, getUserInfo } from '../utils';
import userService from '../services/user.service';

const REQUIRED_FIELD_MESSAGE = 'Este campo es obligatorio.';

const FIELD_LABELS = {
	name: 'Nombre',
	email: 'Email',
	password: 'Contraseña',
};

const reducer = (state, action) => {
	switch (action.type) {
	case 'SAVE_REQUEST':
		return { ...state, saving: true };
	case 'SAVE_SUCCESS':
		return { ...state, saving: false };
	case 'SAVE_FAIL':
		return { ...state, saving: false };
	default:
		return state;
	}
};

const FieldError = ({ message }) =>
	message ? <p className='tw:mt-1 tw:mb-0 tw:text-[12px] tw:text-danger'>{message}</p> : null;

FieldError.propTypes = {
	message: PropTypes.string,
};

function ProfileScreen() {
	const { dispatch: ctxDispatch } = useContext(Store);
	const navigate = useNavigate();
	const userInfo = getUserInfo();

	const [{ saving }, dispatch] = useReducer(reducer, { saving: false });

	const [name, setName] = useState(userInfo ? userInfo.name : '');
	const [email, setEmail] = useState(userInfo ? userInfo.email : '');
	const [password, setPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [fieldErrors, setFieldErrors] = useState({});
	const [formError, setFormError] = useState('');

	if (!userInfo) {
		return <Navigate to='/signin' replace />;
	}

	const roleLabel = ROLE_OPTIONS.find((option) => option.value === userInfo.role)?.label || userInfo.role;

	const clearFieldError = (field) => {
		setFieldErrors((prevErrors) => {
			if (!prevErrors[field]) return prevErrors;
			const nextErrors = { ...prevErrors };
			delete nextErrors[field];
			return nextErrors;
		});
	};

	const validate = () => {
		const errors = {};
		if (!name.trim()) errors.name = REQUIRED_FIELD_MESSAGE;
		if (!email.trim()) errors.email = REQUIRED_FIELD_MESSAGE;
		if ((password || confirmPassword) && password !== confirmPassword) {
			errors.password = 'Las contraseñas no coinciden.';
		}
		return errors;
	};

	const showMissingFields = (errors) => {
		const missingLabels = Object.keys(errors).map((field) => FIELD_LABELS[field] || field);
		const message = errors.password
			? errors.password
			: `Complete el/los siguiente/s campo/s requerido/s: ${missingLabels.join(', ')}.`;
		setFormError(message);
		toast.error(message);
	};

	const submitHandler = async (event) => {
		event.preventDefault();
		if (saving) return;

		const errors = validate();
		if (Object.keys(errors).length > 0) {
			setFieldErrors(errors);
			showMissingFields(errors);
			return;
		}

		const payload = {
			name: name.trim(),
			email: email.trim().toLowerCase(),
		};
		if (password) {
			payload.password = password;
		}

		try {
			dispatch({ type: 'SAVE_REQUEST' });
			const { data } = await userService.edit(userInfo._id, payload);
			dispatch({ type: 'SAVE_SUCCESS' });
			setFormError('');
			setFieldErrors({});
			setPassword('');
			setConfirmPassword('');
			const nextUserInfo = { ...userInfo, ...data };
			ctxDispatch({ type: 'USER_SIGNIN', payload: nextUserInfo });
			localStorage.setItem('userInfo', JSON.stringify(nextUserInfo));
			toast.success('Perfil actualizado Correctamente');
		} catch (err) {
			dispatch({ type: 'SAVE_FAIL' });
			toast.error(getError(err));
		}
	};

	return (
		<EntityList>
			<Helmet>
				<title>Perfil</title>
			</Helmet>
			<PageHead
				breadcrumb={[{ label: 'Inicio', to: '/AdminScreen' }, { label: 'Mi Perfil' }]}
				title='Mi Perfil'
				subtitle='Actualizá tus datos de acceso.'
			/>
			<Form id='profile-editor-form' onSubmit={submitHandler} className='tw:space-y-4'>
				<div className='tw:sticky tw:top-14 tw:z-20 tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-3 tw:rounded-panel tw:border tw:border-border tw:bg-surface/95 tw:px-5 tw:py-3.5 tw:shadow-sm tw:backdrop-blur'>
					<div className='tw:text-[12.5px] tw:text-ink-2'>Actualizá los datos de tu perfil.</div>
					<div className='tw:flex tw:gap-2'>
						<Button icon={<MdArrowBack />} onClick={() => navigate('/AdminScreen')} disabled={saving}>
							Cancelar
						</Button>
						<Button variant='primary' icon={<MdSave />} type='submit' disabled={saving}>
							{saving ? 'Guardando...' : 'Guardar'}
						</Button>
					</div>
				</div>

				{formError && (
					<div className='tw:rounded-[7px] tw:border tw:border-danger tw:bg-danger-soft tw:px-4 tw:py-3 tw:text-[13px] tw:text-danger'>
						{formError}
					</div>
				)}

				<Section title='Datos personales'>
					<div className='tw:grid tw:grid-cols-1 tw:gap-4 md:tw:grid-cols-2'>
						<FormField id='profile-name' label={FIELD_LABELS.name} required>
							<Form.Control
								id='profile-name'
								className={controlClass}
								value={name}
								disabled={saving}
								onChange={(event) => {
									setName(event.target.value);
									clearFieldError('name');
								}}
							/>
							<FieldError message={fieldErrors.name} />
						</FormField>
						<FormField id='profile-email' label={FIELD_LABELS.email} required>
							<Form.Control
								id='profile-email'
								type='email'
								className={controlClass}
								value={email}
								disabled={saving}
								onChange={(event) => {
									setEmail(event.target.value.toLowerCase());
									clearFieldError('email');
								}}
							/>
							<FieldError message={fieldErrors.email} />
						</FormField>
						<FormField id='profile-role' label='Rol'>
							<Form.Control id='profile-role' className={controlClass} value={roleLabel} readOnly />
						</FormField>
					</div>
				</Section>

				<Section title='Cambiar contraseña' description='Dejá los campos vacíos si no querés cambiarla.'>
					<div className='tw:grid tw:grid-cols-1 tw:gap-4 md:tw:grid-cols-2'>
						<FormField id='profile-password' label={FIELD_LABELS.password}>
							<Form.Control
								id='profile-password'
								type='password'
								className={controlClass}
								value={password}
								disabled={saving}
								onChange={(event) => {
									setPassword(event.target.value);
									clearFieldError('password');
								}}
							/>
						</FormField>
						<FormField id='profile-confirm-password' label='Repetir Contraseña'>
							<Form.Control
								id='profile-confirm-password'
								type='password'
								className={controlClass}
								value={confirmPassword}
								disabled={saving}
								onChange={(event) => {
									setConfirmPassword(event.target.value);
									clearFieldError('password');
								}}
							/>
							<FieldError message={fieldErrors.password} />
						</FormField>
					</div>
				</Section>
			</Form>
		</EntityList>
	);
}

export default ProfileScreen;
